import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, TrendingDown, X } from 'lucide-react';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { usePermissions } from '../context/PermissionContext';
import { useSite } from '../context/SiteContext';
import { formatDate } from '../utils/dateUtils';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyMetrics, setKeyMetrics] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Time filter for incident-derived widgets (snapshot, charts, recent list).
  // Key Safety Metrics keep their standard windows (LTIFR is a 12-month
  // rolling metric; PSS/SSEM are point-in-time scores), so the filter only
  // drives /dashboard/stats.
  const [datePreset, setDatePreset] = useState('all'); // '7d'|'30d'|'90d'|'ytd'|'all'|'custom'
  const [customFrom, setCustomFrom] = useState('');    // YYYY-MM-DD
  const [customTo, setCustomTo] = useState('');

  // Welcome banner — show once per browser session so the greeting feels
  // personal but doesn't follow the user around. sessionStorage clears on
  // tab close / explicit logout, so it reappears on the next sign-in.
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return sessionStorage.getItem('dashboard_welcome_seen') !== '1';
    } catch (_e) {
      return true;
    }
  });

  useEffect(() => {
    if (!showWelcome) return;
    // Fade out after 6 seconds and mark as seen so it doesn't return on
    // subsequent dashboard visits in the same tab.
    const timer = setTimeout(() => {
      setShowWelcome(false);
      try { sessionStorage.setItem('dashboard_welcome_seen', '1'); } catch (_e) { /* ignore */ }
    }, 6000);
    return () => clearTimeout(timer);
  }, [showWelcome]);

  // Module access control
  const { isModuleEnabled, hasAnyModule } = useModuleAccess();
  const { hasPermission } = usePermissions();

  // Active site selection — when a site is chosen every widget is scoped to it;
  // when "All Sites" is selected (activeSiteId === null) the dashboard shows
  // cumulative data across all sites. Refetch whenever the selection changes.
  const { activeSiteId } = useSite();

  useEffect(() => {
    // Key safety metrics require incidents:read. Skip the calls entirely for
    // users without it — the backend enforces the same gate (403), so fetching
    // would only produce console noise. `hasPermission` is in the deps so this
    // re-runs once permissions finish loading (they resolve async after mount).
    if (hasPermission('incidents', 'read')) {
      fetchKeyMetrics();
    }
  }, [activeSiteId, hasPermission]);

  useEffect(() => {
    // Incident stats refetch on time-filter changes too; key metrics (above)
    // deliberately don't — their windows are fixed by definition.
    if (hasPermission('incidents', 'read')) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [activeSiteId, hasPermission, datePreset, customFrom, customTo]);

  // Map the selected preset to date_from/date_to (YYYY-MM-DD). 'all' sends no
  // params so the request is identical to the unfiltered one. Dates are
  // formatted in browser-local time (toISOString would shift across UTC).
  const getDateParams = () => {
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    switch (datePreset) {
      case '7d': {
        const d = new Date(today); d.setDate(d.getDate() - 7);
        return { date_from: fmt(d) };
      }
      case '30d': {
        const d = new Date(today); d.setDate(d.getDate() - 30);
        return { date_from: fmt(d) };
      }
      case '90d': {
        const d = new Date(today); d.setDate(d.getDate() - 90);
        return { date_from: fmt(d) };
      }
      case 'ytd':
        return { date_from: `${today.getFullYear()}-01-01` };
      case 'custom': {
        const out = {};
        if (customFrom) out.date_from = customFrom;
        if (customTo) out.date_to = customTo;
        return out;
      }
      default:
        return {};
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (activeSiteId) params.append('site_id', activeSiteId);
      const { date_from, date_to } = getDateParams();
      if (date_from) params.append('date_from', date_from);
      if (date_to) params.append('date_to', date_to);
      const qs = params.toString();
      const response = await axios.get(`${API}/dashboard/stats${qs ? `?${qs}` : ''}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Snapshot cards drill down into the incidents list, carrying the status
  // and the currently selected time period so the list shows the same rows
  // the card counted (IncidentsList reads these params on mount).
  const goToIncidents = (status) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const { date_from, date_to } = getDateParams();
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    const qs = params.toString();
    navigate(`/incidents${qs ? `?${qs}` : ''}`);
  };

  const fetchKeyMetrics = async () => {
    try {
      const siteParam = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const [ltifr, pss, indicators, ssemData] = await Promise.all([
        axios.get(`${API}/dashboard/ltifr-trifr${siteParam}`),
        axios.get(`${API}/dashboard/pss${siteParam}`),
        axios.get(`${API}/dashboard/leading-indicators${siteParam}`),
        axios.get(`${API}/ssem/dashboard${siteParam}`).catch(() => ({ data: null }))
      ]);
      
      setKeyMetrics({
        ltifr: ltifr.data,
        pss: pss.data,
        indicators: indicators.data,
        ssem: ssemData.data
      });
    } catch (error) {
      console.error('Failed to fetch key metrics:', error);
    }
  };

  const openMetricModal = (metric) => {
    setSelectedMetric(metric);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6" data-testid="dashboard">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
            <p
              className={`text-gray-600 mt-1 transition-all duration-700 ease-out overflow-hidden ${
                showWelcome ? 'opacity-100 max-h-8' : 'opacity-0 max-h-0 mt-0'
              }`}
              aria-hidden={!showWelcome}
            >
              Welcome back, {user?.name}
            </p>
          </div>
          {/* Switch back to the new Home dashboard (clears the classic pref
              so `/` defaults to Home again). */}
          <button
            onClick={() => { localStorage.removeItem('saarv_home_pref'); navigate('/dashboard/home'); }}
            className="shrink-0 rounded-lg border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-800 hover:bg-sky-100"
          >
            ✨ Switch to the new Home dashboard →
          </button>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-[#1B5C94] mb-4">QUICK ACTIONS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Report New Incident - Requires safety or incidents module + create permission */}
            {hasAnyModule('safety', 'incidents') && hasPermission('incidents', 'create') && (
              <button
                data-testid="report-hazard-button"
                onClick={() => navigate('/report')}
                className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
              >
                + Report New Incident
              </button>
            )}

            {/* View Incidents List - Requires safety or incidents module + read permission */}
            {hasAnyModule('safety', 'incidents') && hasPermission('incidents', 'read') && (
              <button
                data-testid="view-hazards-button"
                onClick={() => navigate('/incidents')}
                className="bg-gradient-to-r from-[#DAA520] to-[#C47723] text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
              >
                View Incidents List
              </button>
            )}

            {/* Safety Modules - Requires safety module */}
            {isModuleEnabled('safety') && (
              <button
                onClick={() => navigate('/safety')}
                className="bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
                data-testid="safety-hub-button"
              >
                🛡️ Safety Modules
              </button>
            )}

            {/* Executive Dashboard - Only for Admin, Director, HSE Manager */}
            {isModuleEnabled('safety') && ['Admin', 'admin', 'Super Admin', 'Director', 'director', 'HSE Manager', 'hse_manager'].includes(user?.role) && (
              <button
                onClick={() => navigate('/dashboard/executive')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
                data-testid="executive-dashboard-quick-btn"
              >
                📊 Executive Dashboard
              </button>
            )}

            {/* Supervisor Dashboard - For Supervisor role */}
            {isModuleEnabled('safety') && (user?.role === 'Supervisor' || user?.role === 'supervisor') && (
              <button
                onClick={() => navigate('/dashboard/supervisor')}
                className="bg-gradient-to-r from-blue-600 to-blue-800 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
                data-testid="supervisor-dashboard-quick-btn"
              >
                👷 Supervisor Dashboard
              </button>
            )}
          </div>
        </div>

        {/* Key Safety Metrics - Clickable. Incident-derived → requires incidents:read. */}
        {hasPermission('incidents', 'read') && keyMetrics && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-[#1B5C94] mb-4">KEY SAFETY METRICS</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* PSS */}
              <button
                onClick={() => keyMetrics.pss.score !== null && openMetricModal('pss')}
                className={`bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border-l-4 border-purple-500 transition-all text-left ${
                  keyMetrics.pss.score !== null ? 'hover:shadow-lg cursor-pointer' : 'cursor-default opacity-75'
                }`}
                data-testid="pss-metric-card"
              >
                <div className="text-sm text-purple-600 font-semibold mb-1">Predictive Safety Score</div>
                {keyMetrics.pss.score !== null ? (
                  <>
                    <div className="text-4xl font-bold text-purple-700">{keyMetrics.pss.score}</div>
                    <div className="text-xs text-purple-600 mt-1">{keyMetrics.pss.risk_level}</div>
                    {keyMetrics.pss.trend && (
                      <div className="flex items-center mt-2 text-xs text-purple-600">
                        {keyMetrics.pss.trend === 'Improving' ? (
                          <TrendingUp className="w-4 h-4 mr-1" />
                        ) : (
                          <TrendingDown className="w-4 h-4 mr-1" />
                        )}
                        {keyMetrics.pss.trend}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold text-purple-700">No Data</div>
                    <div className="text-xs text-purple-600 mt-1">{keyMetrics.pss.risk_level}</div>
                    <div className="text-xs text-purple-500 mt-2 italic">
                      {keyMetrics.pss.message || 'Start recording safety data'}
                    </div>
                  </>
                )}
              </button>

              {/* LTIFR */}
              <button
                onClick={() => openMetricModal('ltifr')}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-l-4 border-blue-500 hover:shadow-lg transition-all text-left"
                data-testid="ltifr-metric-card"
              >
                <div className="text-sm text-blue-600 font-semibold mb-1">LTIFR (12-Month)</div>
                <div className="text-4xl font-bold text-blue-700">{keyMetrics.ltifr.ltifr_12m_rolling}</div>
                <div className="text-xs text-blue-600 mt-1">Lost Time Injury Frequency</div>
                <div className="text-xs text-blue-600 mt-2">Current: {keyMetrics.ltifr.ltifr_current}</div>
              </button>

              {/* High-Risk Incidents */}
              <button
                onClick={() => openMetricModal('hazards')}
                className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border-l-4 border-orange-500 hover:shadow-lg transition-all text-left"
                data-testid="hazards-metric-card"
              >
                <div className="text-sm text-orange-600 font-semibold mb-1">High-Risk Incidents</div>
                <div className="text-4xl font-bold text-orange-700">{keyMetrics.indicators.high_severity_incidents_open}</div>
                <div className="text-xs text-orange-600 mt-1">Currently Open</div>
                <div className="text-xs text-orange-600 mt-2">Require Attention</div>
              </button>

              {/* SSEM */}
              {(() => {
                const level = keyMetrics?.ssem?.current_overall_level || 0;
                const colorScheme = {
                  0: { bg: 'from-gray-50 to-gray-100', border: 'border-gray-500', text: 'text-gray-600', textBold: 'text-gray-700' },
                  1: { bg: 'from-red-50 to-red-100', border: 'border-red-500', text: 'text-red-600', textBold: 'text-red-700' },
                  2: { bg: 'from-orange-50 to-orange-100', border: 'border-orange-500', text: 'text-orange-600', textBold: 'text-orange-700' },
                  3: { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-500', text: 'text-yellow-600', textBold: 'text-yellow-700' },
                  4: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-500', text: 'text-blue-600', textBold: 'text-blue-700' },
                  5: { bg: 'from-indigo-50 to-indigo-100', border: 'border-indigo-500', text: 'text-indigo-600', textBold: 'text-indigo-700' },
                  6: { bg: 'from-green-50 to-green-100', border: 'border-green-500', text: 'text-green-600', textBold: 'text-green-700' }
                };
                const colors = colorScheme[level];
                
                return (
                  <button
                    onClick={() => openMetricModal('ssem')}
                    className={`bg-gradient-to-br ${colors.bg} rounded-lg p-6 border-l-4 ${colors.border} hover:shadow-lg transition-all text-left`}
                    data-testid="ssem-metric-card"
                  >
                    <div className={`text-sm ${colors.text} font-semibold mb-1`}>SSEM Maturity</div>
                    <div className={`text-4xl font-bold ${colors.textBold}`}>
                      {level > 0 ? `Level ${level}` : 'Not Assessed'}
                    </div>
                    <div className={`text-xs ${colors.text} mt-1`}>Safety Excellence Model</div>
                    <div className={`text-xs ${colors.text} mt-2`}>
                      {level === 1 && 'Reactive Level'}
                      {level === 2 && 'Managed Level'}
                      {level === 3 && 'Systematic Level'}
                      {level === 4 && 'Proactive Level'}
                      {level === 5 && 'Resilient Level'}
                      {level === 6 && 'Transformational Level'}
                      {level === 0 && 'Complete assessment to see level'}
                    </div>
                  </button>
                );
              })()}
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">Click any metric for detailed insights</p>
          </div>
        )}

        {/* Incident Snapshot */}
        {hasPermission('incidents', 'read') && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-1">
            <h2 className="text-xl font-bold text-[#1B5C94]">INCIDENT SNAPSHOT</h2>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                {[
                  { value: '7d', label: '7D' },
                  { value: '30d', label: '30D' },
                  { value: '90d', label: '90D' },
                  { value: 'ytd', label: 'This Year' },
                  { value: 'all', label: 'All Time' },
                  { value: 'custom', label: 'Custom' },
                ].map(p => (
                  <button
                    key={p.value}
                    data-testid={`dashboard-date-preset-${p.value}`}
                    onClick={() => {
                      setDatePreset(p.value);
                      if (p.value !== 'custom') {
                        setCustomFrom('');
                        setCustomTo('');
                      }
                    }}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      datePreset === p.value
                        ? 'bg-[#1B5C94] text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {datePreset === 'custom' && (
                <div className="flex gap-2 items-center">
                  <input
                    type="date"
                    data-testid="dashboard-date-from"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                  />
                  <span className="text-sm text-gray-500">to</span>
                  <input
                    type="date"
                    data-testid="dashboard-date-to"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-4">Selected period applies to the incident snapshot, statistics, and recent incidents below.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div
              className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-l-4 border-green-500 cursor-pointer hover:shadow-md transition-shadow"
              data-testid="open-hazards-card"
              role="button"
              tabIndex={0}
              title="View open incidents"
              onClick={() => goToIncidents('Open')}
              onKeyDown={(e) => e.key === 'Enter' && goToIncidents('Open')}
            >
              <div className="text-3xl font-bold text-green-700">{stats?.open_hazards || 0}</div>
              <div className="text-green-600 font-semibold mt-1">Open Incidents</div>
            </div>
            <div
              className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-6 border-l-4 border-amber-500 cursor-pointer hover:shadow-md transition-shadow"
              data-testid="draft-hazards-card"
              role="button"
              tabIndex={0}
              title="View draft incidents"
              onClick={() => goToIncidents('Draft')}
              onKeyDown={(e) => e.key === 'Enter' && goToIncidents('Draft')}
            >
              <div className="text-3xl font-bold text-amber-700">{stats?.draft_hazards || 0}</div>
              <div className="text-amber-600 font-semibold mt-1">Draft Incidents</div>
            </div>
            <div
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-l-4 border-blue-500 cursor-pointer hover:shadow-md transition-shadow"
              data-testid="closed-hazards-card"
              role="button"
              tabIndex={0}
              title="View closed incidents"
              onClick={() => goToIncidents('Closed')}
              onKeyDown={(e) => e.key === 'Enter' && goToIncidents('Closed')}
            >
              <div className="text-3xl font-bold text-blue-700">{stats?.closed_hazards || 0}</div>
              <div className="text-blue-600 font-semibold mt-1">Closed Incidents</div>
            </div>
            <div
              className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border-l-4 border-red-500 cursor-pointer hover:shadow-md transition-shadow"
              data-testid="overdue-actions-card"
              role="button"
              tabIndex={0}
              title="View overdue incidents"
              onClick={() => goToIncidents('Overdue')}
              onKeyDown={(e) => e.key === 'Enter' && goToIncidents('Overdue')}
            >
              <div className="text-3xl font-bold text-red-700">{stats?.overdue_actions || 0}</div>
              <div className="text-red-600 font-semibold mt-1">Overdue Actions</div>
            </div>
          </div>
        </div>
        )}

        {/* Statistics */}
        {hasPermission('incidents', 'read') && (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Severity */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Incidents by Severity</h3>
            <div className="space-y-3">
              {Object.entries(stats?.by_severity || {}).map(([severity, count]) => (
                <div key={severity} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-4 h-4 rounded-full ${
                        severity === 'Critical'
                          ? 'bg-red-700'
                          : severity === 'High'
                          ? 'bg-red-500'
                          : severity === 'Medium'
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                      }`}
                    ></div>
                    <span className="font-medium text-gray-700">{severity}</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-800">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By Status */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Incidents by Status</h3>
            <div className="space-y-3">
              {Object.entries(stats?.by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">{status}</span>
                  <span className="text-2xl font-bold text-gray-800">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* By Type */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Incidents by Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(stats?.by_type || {}).map(([type, count]) => (
              <div key={type} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="text-xl font-bold text-[#1B5C94]">{count}</div>
                <div className="text-gray-600 text-sm mt-1">{type}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Incidents */}
        {stats?.recent_hazards?.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Recent Incidents</h3>
            <div className="space-y-3">
              {stats.recent_hazards.map((hazard) => (
                <div
                  key={hazard.id}
                  onClick={() => navigate(`/incidents/${hazard.id}`)}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  data-testid={`recent-hazard-${hazard.id}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">{hazard.title || 'Untitled Incident'}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {hazard.date_reported ? formatDate(hazard.date_reported) : 'Date not available'}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        hazard.severity === 'Critical'
                          ? 'bg-red-200 text-red-900'
                          : hazard.severity === 'High'
                          ? 'bg-red-100 text-red-700'
                          : hazard.severity === 'Medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {hazard.severity}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                      {hazard.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}

        {/* Metric Detail Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1B5C94]">
                {selectedMetric === 'pss' && 'Predictive Safety Score (PSS)'}
                {selectedMetric === 'ltifr' && 'LTIFR - Lost Time Injury Frequency Rate'}
                {selectedMetric === 'hazards' && 'High-Risk Incidents'}
                {selectedMetric === 'ssem' && 'SSEM Maturity Level'}
              </DialogTitle>
            </DialogHeader>
            
            {keyMetrics && selectedMetric === 'pss' && (
              <div className="space-y-4">
                <div className="bg-purple-50 p-6 rounded-lg border-2 border-purple-200">
                  <div className="text-center mb-4">
                    <div className="text-5xl font-bold text-purple-700">{keyMetrics.pss.score}/100</div>
                    <div className="text-lg text-purple-600 mt-2">{keyMetrics.pss.risk_level}</div>
                  </div>
                  <div className="flex items-center justify-center mt-3">
                    {keyMetrics.pss.trend === 'Improving' ? (
                      <div className="flex items-center text-green-600">
                        <TrendingUp className="w-5 h-5 mr-2" />
                        <span className="font-semibold">{keyMetrics.pss.trend} Trend</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-orange-600">
                        <TrendingDown className="w-5 h-5 mr-2" />
                        <span className="font-semibold">{keyMetrics.pss.trend} Trend</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Contributing Factors:</h4>
                  <div className="space-y-2">
                    {Object.entries(keyMetrics.pss.contributors).map(([factor, score]) => (
                      <div key={factor} className="flex items-center">
                        <span className="text-sm text-gray-600 w-48 capitalize">{factor.replace('_', ' ')}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-3 mr-3">
                          <div 
                            className="bg-purple-500 h-3 rounded-full" 
                            style={{ width: `${score}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-800 w-12 text-right">{Math.round(score)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>What is PSS?</strong> The Predictive Safety Score combines multiple safety metrics 
                    to provide an overall safety performance indicator. Higher scores indicate better safety performance.
                  </p>
                </div>
              </div>
            )}

            {keyMetrics && selectedMetric === 'ltifr' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-6 rounded-lg text-center">
                    <div className="text-sm text-blue-600 mb-1">Current Month</div>
                    <div className="text-4xl font-bold text-blue-700">{keyMetrics.ltifr.ltifr_current}</div>
                  </div>
                  <div className="bg-blue-50 p-6 rounded-lg text-center">
                    <div className="text-sm text-blue-600 mb-1">12-Month Rolling</div>
                    <div className="text-4xl font-bold text-blue-700">{keyMetrics.ltifr.ltifr_12m_rolling}</div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Monthly Trend (Last 12 Months):</h4>
                  <div className="space-y-2">
                    {keyMetrics.ltifr.monthly_trend.slice(-6).map((month, idx) => (
                      <div key={idx} className="flex items-center">
                        <span className="text-sm text-gray-600 w-32">{month.month}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-3 mr-3">
                          <div 
                            className="bg-blue-500 h-3 rounded-full" 
                            style={{ width: `${Math.min(month.ltifr * 10, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-800 w-12 text-right">{month.ltifr}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {keyMetrics.ltifr.trifr_12m_rolling && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-700 mb-2">
                      <strong>TRIFR (12-Month):</strong> {keyMetrics.ltifr.trifr_12m_rolling}
                    </div>
                    <p className="text-xs text-gray-600">
                      Total Recordable Injury Frequency Rate (includes LTI, Medical Treatment, Restricted Work)
                    </p>
                  </div>
                )}
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>What is LTIFR?</strong> Lost Time Injury Frequency Rate measures the number of lost time injuries 
                    per million hours worked. Lower is better. Industry benchmark: &lt;3.0
                  </p>
                </div>
              </div>
            )}

            {keyMetrics && selectedMetric === 'hazards' && (
              <div className="space-y-4">
                <div className="bg-orange-50 p-6 rounded-lg text-center border-2 border-orange-200">
                  <div className="text-5xl font-bold text-orange-700">{keyMetrics.indicators.high_severity_incidents_open}</div>
                  <div className="text-lg text-orange-600 mt-2">High-Risk Incidents Currently Open</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">High-Risk Observations</div>
                    <div className="text-3xl font-bold text-gray-800">{keyMetrics.indicators.high_risk_observations}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Unsafe Acts (30d)</div>
                    <div className="text-3xl font-bold text-gray-800">{keyMetrics.indicators.unsafe_acts_reported}</div>
                  </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Action Required:</strong> High-risk incidents require immediate attention and mitigation.
                    Review each incident and ensure CAPAs are in place.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      navigate('/incidents');
                    }}
                    className="w-full bg-orange-600 text-white font-bold py-3 rounded-lg hover:bg-orange-700"
                  >
                    View All Incidents
                  </button>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      navigate('/observations');
                    }}
                    className="w-full bg-[#1B5C94] text-white font-bold py-3 rounded-lg hover:bg-[#154a78]"
                  >
                    View Observations
                  </button>
                </div>
              </div>
            )}

            {selectedMetric === 'ssem' && (
              <div className="space-y-4">
                {keyMetrics?.ssem ? (
                  <>
                    {(() => {
                      const level = keyMetrics.ssem.current_overall_level || 0;
                      const modalColorScheme = {
                        0: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', textLight: 'text-gray-600' },
                        1: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', textLight: 'text-red-600' },
                        2: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', textLight: 'text-orange-600' },
                        3: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', textLight: 'text-yellow-600' },
                        4: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', textLight: 'text-blue-600' },
                        5: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', textLight: 'text-indigo-600' },
                        6: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', textLight: 'text-green-600' }
                      };
                      const modalColors = modalColorScheme[level];
                      
                      return (
                        <div className={`${modalColors.bg} p-6 rounded-lg text-center border-2 ${modalColors.border}`}>
                          <div className={`text-5xl font-bold ${modalColors.text}`}>
                            Level {level}
                          </div>
                          <div className={`text-lg ${modalColors.textLight} mt-2`}>
                            {level === 1 && 'Reactive Approach'}
                            {level === 2 && 'Managed Approach'}
                            {level === 3 && 'Systematic Approach'}
                            {level === 4 && 'Proactive Approach'}
                            {level === 5 && 'Resilient Approach'}
                            {level === 6 && 'Transformational Approach'}
                            {level === 0 && 'Not Assessed Yet'}
                          </div>
                        </div>
                      );
                    })()}

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">SSEM Dimensions:</h4>
                      <div className="space-y-2">
                        {keyMetrics.ssem.dimension_breakdown && keyMetrics.ssem.dimension_breakdown.map((dim) => {
                          const barColorScheme = {
                            1: 'bg-red-500',
                            2: 'bg-orange-500',
                            3: 'bg-yellow-500',
                            4: 'bg-blue-500',
                            5: 'bg-indigo-500',
                            6: 'bg-green-500'
                          };
                          
                          return (
                            <div key={dim.dimension} className="flex items-center">
                              <span className="text-sm text-gray-600 w-32">{dim.dimension}</span>
                              <div className="flex-1 flex gap-1 mr-3">
                                {[1, 2, 3, 4, 5, 6].map((l) => (
                                  <div
                                    key={l}
                                    className={`h-6 flex-1 rounded ${
                                      l <= dim.level ? barColorScheme[l] : 'bg-gray-200'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-semibold text-gray-800 w-12 text-right">L{dim.level}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {(() => {
                      const level = keyMetrics.ssem.current_overall_level || 0;
                      const infoBoxColors = {
                        0: 'bg-gray-50',
                        1: 'bg-red-50',
                        2: 'bg-orange-50',
                        3: 'bg-yellow-50',
                        4: 'bg-blue-50',
                        5: 'bg-indigo-50',
                        6: 'bg-green-50'
                      };
                      
                      return (
                        <div className={`${infoBoxColors[level]} p-4 rounded-lg`}>
                          <p className="text-sm text-gray-700">
                            <strong>What is SSEM?</strong> Safety & Sustainability Excellence Model measures organizational maturity 
                            across 8 dimensions on a 6-level scale. Level {level} indicates {
                              level === 1 ? 'a reactive, incident-driven approach.' :
                              level === 2 ? 'a managed, compliance-focused approach.' :
                              level === 3 ? 'a systematic, documented approach to safety.' :
                              level === 4 ? 'a proactive, predictive approach to safety.' :
                              level === 5 ? 'a resilient, adaptive safety culture.' :
                              level === 6 ? 'a transformational, industry-leading approach.' :
                              'that no assessment has been completed yet.'
                            }
                          </p>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No SSEM assessment data available.</p>
                    <Button onClick={() => navigate('/ssem')} className="mt-4">
                      Complete SSEM Assessment
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;